import React from 'react'
import kebabCase from 'lodash/kebabCase'
import dayjs from 'dayjs'

import { BLOG_SLUG } from 'consts'

import {
  ASK_ANVIL_TAGS,
  BLOG_TAGS,
  TAG_CUSTOMER_STORIES,
  TAG_PODCAST,
} from '@anvilco-pkg/sanity'
import Markdown from '@anvilco-pkg/ui/components/Markdown'

import { BlogTestimonial } from './components'
import { getBlogASTRenderer, getHelpASTRenderer } from './htmlAst'
import {
  getModifiedDate,
  getPublishDate,
  isMarkdownRemark,
  isSanityBlogPost,
  isSanityAskAnvil,
  sortPosts,
  isSanityPodcastPage,
} from './helpers'

import getContentElement, { getPlainText } from './sanity'

import { getPagePath } from 'helpers/path'

export const validateSanityArticle = (node, opts) => {
  if (opts?.skipValidation) return
  if (!node) {
    throw new Error('sanityArticle is null.')
  }

  if (!node?.article) {
    throw new Error('sanityArticle has no article object.')
  }

  if (
    !node.article?.tags ||
    !node.article?.slug ||
    !node?._rawArticle?.summary ||
    !node.article?.title
  ) {
    throw new Error(
      `sanityArticle is missing article information. Info passed: ${JSON.stringify(
        node.article,
        null,
        2
      )}`
    )
  }

  if (isSanityBlogPost(node)) {
    if (!node.article?.tags.includes(TAG_CUSTOMER_STORIES) && !node?.author) {
      throw new Error(
        'sanityArticle is not a Customer Story and has no author object. Please add an author object.'
      )
    }
  }
}

export const normalizeSanityArticle = (node, opts) => {
  const tags = node?.article?.tags
    .map((tagKey) => {
      if (isSanityAskAnvil(node))
        return ASK_ANVIL_TAGS.find(({ key }) => key === tagKey).value
      if (isSanityBlogPost(node))
        return BLOG_TAGS.find(({ key }) => key === tagKey).value
      return undefined
    })
    .filter((value) => !!value)

  let primaryTag
  if (isSanityAskAnvil(node)) {
    primaryTag = ASK_ANVIL_TAGS.find(
      ({ key }) => key === node?.article?.primaryTag
    )?.value
  } else if (isSanityBlogPost(node)) {
    primaryTag = BLOG_TAGS.find(
      ({ key }) => key === node?.article?.primaryTag
    )?.value
  }

  return {
    gatsbyId: node.id,
    slug: node?.article?.slug,
    title: node?.article?.title,
    metaTitle: node?.article?.seoData?.metaTitle,
    socialTitle: node?.article?.seoData?.socialTitle,
    summary: {
      reactComponent: getContentElement({
        content: node._rawArticle.summary,
      }),
      plainText: getPlainText(node._rawArticle.summary),
    },
    metaDescription: node?.article?.seoData?.metaDescription,
    tags,
    primaryTag,
    keywords: node?.article?.keywords,
    type: node?.article?.type,
  }
}

export const normalizeSanityPodcastPage = (node) => {
  const tag = BLOG_TAGS.find(({ key }) => key === TAG_PODCAST).value
  return {
    gatsbyId: node.id,
    slug: kebabCase(node.podcast.title.toLowerCase()),
    title: node.podcast.title,
    metaTitle: node.seoData?.metaTitle,
    socialTitle: node.seoData?.socialTitle,
    summary: {
      reactComponent: getContentElement({
        content: node.podcast._rawSummary,
      }),
      plainText: getPlainText(node.podcast._rawSummary),
    },
    metaDescription: node.seoData?.metaDescription,
    tags: [tag],
    primaryTag: tag,
    video: node.podcast.video,
  }
}

const validateSanityBlogPost = (node, opts) => {
  validateSanityArticle(node, opts)
  if (!opts?.indexPage && !node?._rawContent) {
    throw new Error(`No content specified for ${node.article.title}`)
  }
  if (!node?.heroImage?.asset?.gatsbyImageData) {
    throw new Error(
      `No hero image specified for sanityArticle: ${node.article.title}`
    )
  }
}

/**
 * node - the gatsby object that contains all the information for the blog post.
 *        it can be markdownRemark, sanityBlogPost, anything.
 */
// TODO: test this
export const normalizeBlogPost = (node, opts) => {
  const { sanityAuthorDocument, sanityHeroImage, indexPage, airtableRecord } =
    opts || {}

  const airtablePublishDate = airtableRecord?.Publish_Date
  const airtableModifiedDate = airtableRecord?.Last_Modified

  // this one is where type is going to be needed... but probably the only place? so we can normalize the data
  // for each domain ('Blog', 'Help', 'Case', 'Form')
  if (isMarkdownRemark(node)) {
    validateMarkdownBlogPost(node, opts)

    const htmlAst = sanitizeHtmlAst(node.htmlAst)
    return {
      gatsbyId: node.id,
      slug: node.frontmatter.slug,
      title: node.frontmatter.title,
      metaTitle: node.frontmatter.metaTitle,
      socialTitle: node.frontmatter.socialTitle,
      summary: {
        reactComponent: <Markdown>{node.frontmatter.summary}</Markdown>,
        plainText: node.frontmatter.summary,
      },
      metaDescription: node.frontmatter.metaDescription,
      modifiedDate: getModifiedDate({
        airtableModifiedDate,
        airtablePublishDate,
      }),
      publishDate: getPublishDate({
        airtablePublishDate,
      }),
      primaryTag: node.frontmatter.tags[0],
      tags: node.frontmatter.tags,
      type: node.frontmatter.type,
      keywords: node.frontmatter.keywords,
      author:
        node.frontmatter.type === 'Blog'
          ? {
              _id: sanityAuthorDocument?._id || node.frontmatter.author,
              name: sanityAuthorDocument?.person
                ? `${sanityAuthorDocument?.person?.firstName} ${sanityAuthorDocument?.person?.lastName}`
                : null,
              image: sanityAuthorDocument?.person
                ? sanityAuthorDocument?.person?.image
                : null,
            }
          : null,
      heroImage: {
        gatsbyImageData:
          sanityHeroImage?.gatsbyImageData ||
          node.frontmatter.image?.childImageSharp?.gatsbyImageData,
        publicURL: sanityHeroImage?.url || node.frontmatter.image?.publicURL,
        altText: sanityHeroImage?.title || node?.frontmatter?.imageAltText,
      },
      content: !indexPage && getBlogASTRenderer(htmlAst),
      industry: node?.frontmatter?.industry,
    }
  }

  if (isSanityPodcastPage(node)) {
    const pageData = normalizeSanityPodcastPage(node)
    return {
      ...pageData,
      gatsbyId: node.id,
      summary: {
        plainText: getPlainText(node.podcast._rawSummary),
      },
      publishDate: dayjs(node.podcast.publishedDate),
      modifiedDate: dayjs(node.podcast.publishedDate),
      author: {
        _id: node?.podcast?.guest?._id,
        name: `${node?.podcast?.guest?.person?.firstName} ${node?.podcast?.guest?.person?.lastName}`,
        image: node?.podcast?.guest?.person?.image,
      },
      heroImage: {
        gatsbyImageData: node.podcast.coverImage?.asset?.gatsbyImageData,
        publicURL: node.podcast.coverImage?.asset?.url,
      },
      content: getContentElement({
        content: node.podcast._rawTranscript,
      }),
    }
  }

  if (isSanityBlogPost(node)) {
    validateSanityBlogPost(node, opts)
    const article = normalizeSanityArticle(node, opts)

    const renderBlogTestimonial = ({ value }) => {
      return (
        <BlogTestimonial
          author={`${value.quoteAuthor.firstName} ${value.quoteAuthor.lastName}`}
          title={`${value.quoteAuthor.jobTitle}, ${value.quoteAuthor.organization?.organizationName}`}
        >
          {value.quote.map((quoteChild, i) => (
            <React.Fragment key={quoteChild._key}>
              {quoteChild.children.map((child) => child.text)}
              {i < value.quote.length - 1 && <br />}
            </React.Fragment>
          ))}
        </BlogTestimonial>
      )
    }

    return {
      ...article,
      gatsbyId: node.id,
      summary: {
        reactComponent: getContentElement({
          content: node._rawArticle.summary,
          renderSanityTypeAs: {
            type: 'testimonial',
            render: renderBlogTestimonial,
          },
        }),
        plainText: getPlainText(node._rawArticle.summary),
      },
      modifiedDate: getModifiedDate({
        airtableModifiedDate,
        airtablePublishDate,
      }),
      publishDate: getPublishDate({
        airtablePublishDate,
      }),
      author: !node?.article?.tags?.some(
        (tagKey) => tagKey === TAG_CUSTOMER_STORIES
      )
        ? {
            _id: node?.author?._id,
            name: `${node?.author?.person?.firstName} ${node?.author?.person?.lastName}`,
            image: node?.author?.person?.image,
          }
        : null,
      // will have alt text, but probably another normalizer for images too
      heroImage: {
        gatsbyImageData: node.heroImage?.asset?.gatsbyImageData,
        publicURL: node.heroImage?.asset?.url,
      },
      content:
        !indexPage &&
        getContentElement({
          content: node._rawContent,
          renderSanityTypeAs: {
            type: 'testimonial',
            render: renderBlogTestimonial,
          },
        }),
    }
  }
}

// htmlAst comes from gatsby-node, which does some manipulation based on allll the help articles
export const normalizeHelpPost = (node, htmlAst, opts) => {
  if (isMarkdownRemark(node)) {
    // TODO: validate markdownRemark node here

    return {
      gatsbyId: node.id,
      slug: node.frontmatter.slug,
      title: node.frontmatter.title,
      summary: {
        reactComponent: <Markdown>{node.frontmatter.summary}</Markdown>,
        plainText: node.frontmatter.summary,
      },
      tagline: node.frontmatter.tagline,
      tags: node.frontmatter.tags,
      // TODO: check primaryTag if it exists
      // do we really need type and format here? idt so
      type: node.frontmatter.type,
      format: node.frontmatter.format,
      articleWidth: node.frontmatter.articleWidth,
      content: !opts?.indexPage && getHelpASTRenderer(htmlAst),
    }
  }
}

const validateAskAnvilPost = (node, opts) => {
  validateSanityArticle(node, opts)
  if (!opts?.indexPage && !node?._rawContent) {
    throw new Error(`No content specified for ${node.article.title}`)
  }
}

export const normalizeAskAnvilPost = (node, opts) => {
  const { indexPage } = opts || {}
  validateAskAnvilPost(node, opts)
  const article = normalizeSanityArticle(node, opts)
  return {
    ...article,
    gatsbyId: node.id,
    content:
      !indexPage &&
      getContentElement({
        content: node._rawContent,
      }),
  }
}

function validateMarkdownBlogPost (node, opts) {
  if (
    !(
      opts?.sanityHeroImage?.gatsbyImageData ||
      node.frontmatter.image?.childImageSharp?.gatsbyImageData
    )
  ) {
    throw new Error(
      `No hero image specified for markdownBlogPost: ${node.frontmatter.title}`
    )
  }
}

// at the dayjs, removes parent p tag for custom 'testimonial' components
// which preserves statically generated pages. If there is invalid HTML,
// React/Gatsby will force a client render. No bueno
function sanitizeHtmlAst (htmlAst) {
  const isTestimonial = (child) =>
    child?.tagName === 'p' && child?.children?.[0]?.tagName === 'testimonial'

  const children = htmlAst?.children?.map((child) => {
    if (isTestimonial(child)) return child.children[0]
    return child
  })

  return {
    ...htmlAst,
    children,
  }
}

// NOTE: should create a BlogPostHelper class that is constructer like WeldHelper, CastHelper, etc.
// from the app. This will allow us to have a normalized API for all blog posts, as well as
// an easy way to interact with blog post data.
export function getBlogPostPaths (post) {
  const paths = post.tags.reduce((acc, tag, index) => {
    const currentPath = getPagePath({
      prefixSlug: BLOG_SLUG,
      tagSlug: kebabCase(tag),
      pageSlug: post.slug,
    })

    if (index === 0) {
      return { primaryPath: currentPath, otherPaths: [] }
    }

    return acc.otherPaths.push(currentPath)
  }, {})

  return paths
}

function mergeAndSortPosts (listOfArrays, opts) {
  let singleArray = []
  listOfArrays.forEach((array) => {
    singleArray = singleArray.concat(array)
  })

  return singleArray.sort(opts?.sort)
}

export function getSanityHeroImageForMarkdownPost (
  sanityImagesNodes,
  markdownPostTitle
) {
  return sanityImagesNodes.find(
    (sanityImageNode) =>
      sanityImageNode?.title === markdownPostTitle ||
      sanityImageNode?.description === markdownPostTitle
  )
}

/**
 * markdown:
 * - nodes - required
 * - opts - optional options to pass to normalizeBlogPost
 *
 * sanity:
 * - nodes - required
 * - opts - optional options to pass to normalizeBlogPost
 */
export function mergeMdAndSanityPosts ({
  markdown,
  sanity,
  podcasts,
  sanityImageNodes,
  airtableRecords,
}) {
  const sanityBlogPosts = sanity?.nodes?.map((node) => {
    const opts = {
      ...sanity.opts,
      airtableRecord: airtableRecords?.find(
        ({ data, recordId }) => recordId === node?.article?.airtableId
      )?.data,
    }
    return normalizeBlogPost(node, opts)
  })

  const markdownPosts = markdown?.nodes?.map((node) => {
    const sanityHeroImage = sanityImageNodes
      ? getSanityHeroImageForMarkdownPost(
          sanityImageNodes,
          node?.frontmatter?.title
        )
      : undefined

    const opts = {
      ...markdown?.opts,
      sanityHeroImage,
      airtableRecord: airtableRecords?.find(
        ({ data, recordId }) => recordId === node?.frontmatter?.airtableId
      )?.data,
    }

    return normalizeBlogPost(node, opts)
  })

  const podcastPages =
    podcasts?.nodes?.map((node) => {
      return normalizeBlogPost(node)
    }) ?? []

  return mergeAndSortPosts([markdownPosts, sanityBlogPosts, podcastPages], {
    sort: sortPosts,
  })
}
